import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import useAuthentication from "@hooks/useAuthentication";
import usePopup from "../../hooks/usePopup";
import CustomTableBody from "../../utilis/component/CustomTableBody";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import {
  getAllDocumentsByPatientMasterId,
  getViewDetails,
} from "../../EMR/components/ViewDetails/services";
import {
  geDisChargeSummary,
  geDisChargeSummaryv2,
} from "../../EMR/components/ConsenForms/services";
import {
  splitPatientDocumentsPayload,
  UploadedDocumentsSection,
  VIEW_DETAILS_TABLE_HEADING,
} from "../../EMR/components/ViewDetails/patientDocumentsShared";
import DetailsTab from "../../EMR/components/ViewDetails/DetailsTab";

type InsuranceSlice = {
  insurance_name?: string;
  inPlan_name?: string;
} | null;

type Props = {
  patientMasterId: number;
  apiEncounterId: number | null;
  selectedEncounterId: number | null;
  insurancePatient?: InsuranceSlice;
};

const PatientHistoryDocumentsTab = ({
  patientMasterId,
  apiEncounterId,
  selectedEncounterId,
  insurancePatient,
}: Props) => {
  const { user } = useAuthentication();
  const { showSnackbar } = usePopup();
  const [detailsEncounterId, setDetailsEncounterId] = useState<number | null>(
    null,
  );
  const [selectedDischargeRow, setSelectedDischargeRow] = useState(-1);

  const resolvePdfPayload = (raw: unknown): string | null => {
    if (typeof raw === "string") return raw;
    if (raw && typeof raw === "object") {
      const o = raw as Record<string, unknown>;
      if (typeof o.file === "string") return o.file;
      if (typeof o.content === "string") return o.content;
    }
    return null;
  };

  const displayFile = (file: unknown, title: string) => {
    const payload = resolvePdfPayload(file);
    if (!payload) {
      showSnackbar({
        message: "Failed to load file !",
        open: true,
        variant: "error",
        duration: 2000,
      });
      return;
    }
    try {
      const byteCharacters = atob(payload);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
      }
      const newBlob = new Blob(byteArrays, { type: "application/pdf" });
      const createdBlobUrl = URL.createObjectURL(newBlob);
      const newTab = window.open();
      if (newTab) {
        newTab.document.title = title;
        newTab.location.href = createdBlobUrl;
      }
    } catch {
      showSnackbar({
        message: "Failed to load file !",
        open: true,
        variant: "error",
        duration: 2000,
      });
    }
  };

  const clinicId = user?.clinicId as number | undefined;

  const {
    mutate: viewDetails,
    isLoading: detailsLoading,
    data: details,
  } = useMutation(getViewDetails, {
    onError: (error: unknown) => {
      const err = error as {
        response?: { data?: { responseMessage?: string } };
        message?: string;
      };
      showSnackbar({
        message: err?.response?.data?.responseMessage || err?.message || "",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });

  const {
    mutate: fetchAllPatientDocuments,
    isLoading: allDocsLoading,
    data: allDocsData,
  } = useMutation(getAllDocumentsByPatientMasterId, {
    onSuccess: (data) => {
      if (data.responseIndicator !== "success" && data.responseMessage) {
        showSnackbar({
          message: data.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (error: unknown) => {
      const err = error as {
        response?: { data?: { responseMessage?: string } };
        message?: string;
      };
      showSnackbar({
        message: err?.response?.data?.responseMessage || err?.message || "",
        open: true,
        variant: "error",
        duration: 3000,
      });
    },
  });

  useEffect(() => {
    if (!patientMasterId || !apiEncounterId || !clinicId) return;
    viewDetails({
      clinicId,
      enId: apiEncounterId,
      pmasterId: patientMasterId,
    });
  }, [patientMasterId, apiEncounterId, clinicId, viewDetails]);

  useEffect(() => {
    if (!patientMasterId || !clinicId) return;
    fetchAllPatientDocuments({ patientMasterId, clinicId });
  }, [patientMasterId, clinicId, fetchAllPatientDocuments]);

  const { registration: registrationDocs, encounter: encounterDocsRaw } =
    allDocsData?.responseIndicator === "success"
      ? splitPatientDocumentsPayload(allDocsData.response)
      : { registration: [], encounter: [] };

  const encounterDocs = useMemo(() => {
    if (!selectedEncounterId) return encounterDocsRaw;
    return encounterDocsRaw.filter(
      (d) => Number(d.encounterId) === Number(selectedEncounterId),
    );
  }, [encounterDocsRaw, selectedEncounterId]);

  const { mutate: downloadeDisCharge, isLoading: disChargeLoading } =
    useMutation(geDisChargeSummary, {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          displayFile(result.response, "Discharge Summary form");
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 2000,
          });
        }
      },
    });

  const { mutate: downloadeDisChargev2, isLoading: disChargeLoadingv2 } =
    useMutation(geDisChargeSummaryv2, {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          displayFile(result.response, "Discharge Summary form");
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 2000,
          });
        }
      },
    });

  if (!patientMasterId || patientMasterId <= 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Patient information is missing.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {detailsEncounterId != null ? (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setDetailsEncounterId(null)}
            sx={{ mb: 1 }}
          >
            Back
          </Button>
          <DetailsTab
            id={detailsEncounterId}
            encounterId={detailsEncounterId}
          />
        </Paper>
      ) : (
        <>
          {!apiEncounterId ? (
            <Typography variant="body2" color="text.secondary">
              No encounter available to load registration list.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="Encounter documents">
                <TableHead>
                  <TableRow>
                    {VIEW_DETAILS_TABLE_HEADING.map((title) => (
                      <TableCell key={title}>
                        <strong>{title}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <CustomTableBody
                  alert={"No records"}
                  dataLoading={detailsLoading}
                  isData={Boolean(details?.response?.length)}
                  totalSpan={{ col: VIEW_DETAILS_TABLE_HEADING.length, row: 5 }}
                >
                  {(details?.response || []).map(
                    (
                      item: {
                        createdDate: string;
                        PatientID: string;
                        clcn_name: string;
                        dept_name: string;
                        encounterID: number;
                      },
                      i: number,
                    ) => (
                      <TableRow
                        key={`enc-${item.encounterID}-${i}`}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{item.createdDate}</TableCell>
                        <TableCell>{item.PatientID}</TableCell>
                        <TableCell>{item.clcn_name}</TableCell>
                        <TableCell>{item.dept_name}</TableCell>
                        <TableCell>
                          <IconButton
                            disabled={disChargeLoading || disChargeLoadingv2}
                            onClick={() => {
                              user?.codetype == "CPT"
                                ? downloadeDisCharge({
                                    id: item.encounterID,
                                    tenetClinicId: Number(user?.tenantClinicId),
                                  })
                                : downloadeDisChargev2({
                                    id: item.encounterID,
                                    insname: String(
                                      insurancePatient?.inPlan_name ?? "",
                                    ),
                                  });
                              setSelectedDischargeRow(i);
                            }}
                            color="primary"
                            size="small"
                          >
                            {(disChargeLoading || disChargeLoadingv2) &&
                            selectedDischargeRow === i ? (
                              <CircularProgress size={18} />
                            ) : (
                              <LocalPrintshopIcon />
                            )}
                          </IconButton>
                          <IconButton
                            onClick={() =>
                              setDetailsEncounterId(item.encounterID)
                            }
                            color="success"
                            size="small"
                          >
                            <RemoveRedEyeIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </CustomTableBody>
              </Table>
            </TableContainer>
          )}

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 1 }}>
            Portal documents
          </Typography>
          {allDocsLoading ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <UploadedDocumentsSection
                sectionTitle="Registration"
                docs={registrationDocs}
              />
              <UploadedDocumentsSection
                sectionTitle="Encounter"
                docs={encounterDocs}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PatientHistoryDocumentsTab;
